/**
 * Power Flow model — turns flat SignalK electrical paths (Victron/Venus via Cerbo plugin)
 * into a Grid → Inverter/Charger → AC loads / Battery / Solar picture.
 *
 * Pure functions only (no imports) so they can be unit tested from backend/scripts/qa/run-unit.js.
 *
 * Product rules:
 *  - Data priority: live WebSocket → database (signalk_latest) → demo sample.
 *  - Concrete instance IDs are read with EXACT paths (e.g. electrical.batteries.279.voltage).
 *    Instance selection happens in discovery/config, never by silently substituting another bank.
 *  - SignalK SI units: W, A, V, Hz, ratio (SOC 0..1), seconds, Kelvin, Joules.
 */

export const FLOW_THRESHOLD_W = 15;
export const LIVE_MAX_AGE_MS = 15 * 60 * 1000;
const MAX_TIME_REMAINING_S = 60 * 24 * 3600; // ignore "infinite" idle values

const PHASE_LETTERS = ['A', 'B', 'C'];

function parseNum(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function isPresent(v) {
  return v !== null && v !== undefined && v !== '' && v !== '--';
}

function ageMs(ts, now) {
  if (!ts) return Infinity;
  const t = new Date(ts).getTime();
  return Number.isNaN(t) ? Infinity : Math.max(0, now - t);
}

/**
 * Raw (unformatted, SI) reader with live → database priority.
 * `source` of each reading: 'live' | 'stale' | 'database'.
 */
export function createPowerReader({
  liveData = {},
  signalKFlat = {},
  vesselStreamConnected = false,
  now = Date.now(),
} = {}) {
  const classify = (ts) => {
    if (!vesselStreamConnected) return 'database';
    return ageMs(ts, now) <= LIVE_MAX_AGE_MS ? 'live' : 'stale';
  };
  return function read(path) {
    const l = liveData[path];
    if (l && isPresent(l.value) && (vesselStreamConnected || ageMs(l.timestamp, now) <= LIVE_MAX_AGE_MS)) {
      return {
        value: l.value,
        units: l.units || l.unit || '',
        timestamp: l.timestamp || null,
        source: classify(l.timestamp),
      };
    }
    const d = signalKFlat[path];
    if (d && isPresent(d.value)) {
      return {
        value: d.value,
        units: d.units || d.unit || '',
        timestamp: d.timestamp || null,
        source: classify(d.timestamp),
      };
    }
    return null;
  };
}

export function listPowerPaths(liveData = {}, signalKFlat = {}) {
  return Array.from(new Set([...Object.keys(liveData || {}), ...Object.keys(signalKFlat || {})]));
}

/** True when the vessel publishes enough electrical data for a meaningful power-flow view. */
export function hasPowerData(paths) {
  return paths.some((p) =>
    /^electrical\.(inverters\.[^.]+\.(acin|acout)\.power|ac\.[^.]+\.total\.realPower|solar\.[^.]+\.panelPower|venus\.(dcPower|totalPanelPower)|batteries\.[^.]+\.(voltage|current|power))$/.test(
      p
    )
  );
}

function instanceIds(paths, group, leafTest) {
  const prefix = `electrical.${group}.`;
  const ids = new Set();
  for (const p of paths) {
    if (!p.startsWith(prefix)) continue;
    const rest = p.slice(prefix.length);
    const dot = rest.indexOf('.');
    if (dot <= 0) continue;
    const id = rest.slice(0, dot);
    if (!leafTest || leafTest(rest.slice(dot + 1))) ids.add(id);
  }
  return Array.from(ids).sort((a, b) => {
    const na = parseInt(a, 10);
    const nb = parseInt(b, 10);
    if (!Number.isNaN(na) && !Number.isNaN(nb) && String(na) === a && String(nb) === b) return na - nb;
    return a.localeCompare(b);
  });
}

export function energyToKwh(value, units) {
  const n = parseNum(value);
  if (n === null) return null;
  if (String(units || '').toLowerCase() === 'kwh') return n;
  return n / 3.6e6; // SignalK SI energy is Joules
}

function socToPercent(v) {
  const n = parseNum(v);
  if (n === null) return null;
  return n <= 1 ? n * 100 : n;
}

function flowDirection(watts) {
  if (watts === null || watts === undefined) return 'none';
  if (watts > FLOW_THRESHOLD_W) return 'forward';
  if (watts < -FLOW_THRESHOLD_W) return 'reverse';
  return 'none';
}

function sum(values) {
  const nums = values.filter((v) => v !== null && v !== undefined);
  return nums.length ? nums.reduce((a, b) => a + b, 0) : null;
}

function titleCase(s) {
  if (!isPresent(s)) return null;
  const t = String(s).replace(/[_-]+/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Decide which `electrical.ac.N` is the AC input (grid/shore) and which is the AC output (loads)
 * by matching their total power to the inverter acin/acout reference sums.
 * Ties (e.g. both ~equal) fall back to: grid = highest numeric id (matches the Venus SignalK plugin).
 */
export function detectAcRoles(acEntries, gridRefW, loadRefW, swap = false) {
  const ids = acEntries.map((e) => e.id);
  let roles;
  if (ids.length === 0) roles = { gridId: null, loadsId: null };
  else if (ids.length === 1) roles = { gridId: ids[0], loadsId: null };
  else {
    const byId = Object.fromEntries(acEntries.map((e) => [e.id, e.totalW]));
    const sorted = [...ids].sort((a, b) => {
      const na = parseInt(a, 10);
      const nb = parseInt(b, 10);
      return !Number.isNaN(na) && !Number.isNaN(nb) ? nb - na : b.localeCompare(a);
    });
    let best = null;
    for (const g of sorted) {
      for (const l of sorted) {
        if (g === l) continue;
        let cost = 0;
        if (gridRefW !== null && byId[g] !== null) cost += Math.abs(byId[g] - gridRefW);
        if (loadRefW !== null && byId[l] !== null) cost += Math.abs(byId[l] - loadRefW);
        if (best === null || cost < best.cost - 1e-9) best = { gridId: g, loadsId: l, cost };
      }
    }
    roles = { gridId: best.gridId, loadsId: best.loadsId };
  }
  if (swap && roles.gridId && roles.loadsId) {
    return { gridId: roles.loadsId, loadsId: roles.gridId };
  }
  return roles;
}

/**
 * Estimated conversion efficiency across the AC/DC system.
 * inputs  = grid import + solar + battery discharge
 * outputs = AC loads + DC loads + battery charge (battery power is net of DC loads, so all three add up)
 * Returns nulls when input power is too low for a meaningful ratio.
 */
export function computeEfficiency({ gridW, solarW, loadsW, batteryW, dcLoadsW }) {
  const inW = Math.max(0, gridW || 0) + Math.max(0, solarW || 0) + Math.max(0, -(batteryW || 0));
  const outW = Math.max(0, loadsW || 0) + Math.max(0, dcLoadsW || 0) + Math.max(0, batteryW || 0);
  if (inW < 50) return { efficiencyPct: null, lossW: null, inW, outW };
  const lossW = Math.max(0, inW - outW);
  const efficiencyPct = Math.min(100, (outW / inW) * 100);
  return { efficiencyPct, lossW, inW, outW };
}

/**
 * Derive what the user considers "house" and "starter" from their own saved dashboard widgets.
 * widgets: [{ widget_name, data_path, section_name }]
 */
export function deriveDashboardHints(widgets = []) {
  const hints = { house: null, starterVoltagePath: null, starterChargerId: null };
  const house = {};
  widgets.forEach((w) => {
    const path = String(w?.data_path || '');
    const label = `${w?.widget_name || ''} ${w?.section_name || ''}`;
    const bat = path.match(/^electrical\.batteries\.([^.]+)\.(voltage|current|capacity\.stateOfCharge)$/);
    if (bat && /house/i.test(label)) {
      const key = bat[2] === 'voltage' ? 'voltageId' : bat[2] === 'current' ? 'currentId' : 'socId';
      if (!house[key]) house[key] = bat[1];
    }
    if (/start/i.test(label)) {
      if (/^electrical\.batteries\.[^.]+\.voltage$/.test(path) && !hints.starterVoltagePath) {
        hints.starterVoltagePath = path;
      }
      const ch = path.match(/^electrical\.chargers\.([^.]+)\./);
      if (ch && !hints.starterChargerId) hints.starterChargerId = ch[1];
    }
  });
  if (house.voltageId || house.currentId || house.socId) hints.house = house;
  return hints;
}

function pickBatteryId(paths, read, configId) {
  const ids = instanceIds(paths, 'batteries');
  const info = ids.map((id) => {
    const p = (s) => `electrical.batteries.${id}.${s}`;
    const hasSoc = Boolean(read(p('capacity.stateOfCharge')));
    const hasCur = Boolean(read(p('current')) || read(p('power')));
    const hasV = Boolean(read(p('voltage')));
    return {
      id,
      hasSoc,
      hasCur,
      hasV,
      name: read(p('name'))?.value ?? null,
      score: (hasSoc ? 4 : 0) + (hasCur ? 2 : 0) + (hasV ? 1 : 0) + (id === '0' ? 3 : 0),
    };
  });
  const choices = info
    .filter((i) => i.hasV && (i.hasSoc || i.hasCur))
    .map((i) => ({ id: i.id, label: i.name ? `${i.name} (${i.id})` : `Bank ${i.id}`, hasSoc: i.hasSoc }));
  let selected = null;
  if (configId && ids.includes(configId)) selected = configId;
  else if (info.length) selected = [...info].sort((a, b) => b.score - a.score)[0].id;
  return { selected, choices };
}

/**
 * Build the whole picture. `paths` = all known path strings, `read(path)` = raw reader.
 * config: { batteryId?: string, swapAc?: boolean }
 */
export function buildPowerFlowModel(paths, read, config = {}) {
  const used = [];
  const rd = (path) => {
    const r = read(path);
    if (r) used.push(r);
    return r;
  };
  const num = (path) => {
    const r = rd(path);
    return r ? parseNum(r.value) : null;
  };
  const str = (path) => {
    const r = rd(path);
    return r && isPresent(r.value) ? String(r.value) : null;
  };

  // ---- Inverter / chargers (Multi, Quattro, MultiRS...) ----
  const inverterIds = instanceIds(paths, 'inverters', (leaf) => /^(acin|acout)\./.test(leaf));
  const inverters = inverterIds.map((id) => {
    const p = (s) => `electrical.inverters.${id}.${s}`;
    return {
      id,
      name: str(p('name')) || `Inverter ${id}`,
      mode: titleCase(str(p('inverterMode'))),
      acInW: num(p('acin.power')),
      acInV: num(p('acin.voltage')),
      acInA: num(p('acin.current')),
      acInLimitA: num(p('acin.currentLimit')),
      acOutW: num(p('acout.power')),
      acOutV: num(p('acout.voltage')),
      acOutA: num(p('acout.current')),
      acOutHz: num(p('acout.frequency')),
      acInHz: num(p('acin.frequency')),
      gridAvailable: num(p('acState.acIn1Available')),
      dcAmps: num(`electrical.chargers.${id}.current`),
    };
  });

  const invGridW = sum(inverters.map((i) => i.acInW));
  const invLoadsW = sum(inverters.map((i) => i.acOutW));

  // ---- AC buses (Venus SignalK plugin: electrical.ac.N.*) ----
  const acIds = instanceIds(paths, 'ac', (leaf) => leaf === 'total.realPower' || leaf.startsWith('phase.'));
  const acEntries = acIds.map((id) => {
    const phases = [];
    PHASE_LETTERS.forEach((letter, idx) => {
      const p = (s) => `electrical.ac.${id}.phase.${letter}.${s}`;
      const v = num(p('lineNeutralVoltage'));
      const a = num(p('current'));
      const w = num(p('realPower'));
      if (v !== null || a !== null || w !== null) {
        phases.push({ label: `L${idx + 1}`, volts: v, amps: a, watts: w, hz: num(p('frequency')) });
      }
    });
    const totalW = num(`electrical.ac.${id}.total.realPower`) ?? sum(phases.map((ph) => ph.watts));
    return { id, totalW, phases };
  });
  const roles = detectAcRoles(acEntries, invGridW, invLoadsW, Boolean(config.swapAc));
  const gridAc = acEntries.find((e) => e.id === roles.gridId) || null;
  const loadsAc = acEntries.find((e) => e.id === roles.loadsId) || null;

  const gridW = invGridW ?? gridAc?.totalW ?? null;
  const loadsW = invLoadsW ?? loadsAc?.totalW ?? null;

  const acSource = str('electrical.venus.acSource') || str('electrical.venus.0.acin.acSource');
  const grid = {
    watts: gridW,
    sourceLabel: titleCase(acSource) || 'Grid',
    phases: gridAc?.phases || [],
    available: inverters.some((i) => i.gridAvailable === 1)
      ? true
      : inverters.some((i) => i.gridAvailable === 0)
        ? false
        : null,
    hz: gridAc?.phases?.[0]?.hz ?? inverters.find((i) => i.acInHz !== null)?.acInHz ?? null,
  };
  const loads = { watts: loadsW, phases: loadsAc?.phases || [] };

  // ---- Solar ----
  const solarIds = instanceIds(paths, 'solar', (leaf) => leaf === 'panelPower' || leaf === 'yieldToday');
  const solarUnits = solarIds.map((id) => {
    const p = (s) => `electrical.solar.${id}.${s}`;
    const y = rd(p('yieldToday'));
    return {
      id,
      name: str(p('name')) || `Solar ${id}`,
      watts: num(p('panelPower')),
      volts: num(p('panelVoltage')),
      state: titleCase(str(p('controllerMode'))),
      yieldTodayKwh: y ? energyToKwh(y.value, y.units) : null,
    };
  });
  const solarW = sum(solarUnits.map((s) => s.watts)) ?? num('electrical.venus.totalPanelPower');
  const solar = {
    watts: solarW,
    yieldTodayKwh: sum(solarUnits.map((s) => s.yieldTodayKwh)),
    controllers: solarUnits,
  };

  // ---- Battery ----
  const { selected: batteryId, choices: batteryChoices } = pickBatteryId(paths, read, config.batteryId);
  const allBatteryIds = instanceIds(paths, 'batteries');
  const hh = config.hints?.house || null;
  const hhValid =
    !config.batteryId &&
    hh &&
    [hh.voltageId, hh.currentId, hh.socId].filter(Boolean).every((id) => allBatteryIds.includes(id));
  const src = hhValid
    ? { v: hh.voltageId || hh.currentId, i: hh.currentId || hh.voltageId, s: hh.socId }
    : { v: batteryId, i: batteryId, s: batteryId };
  let battery = null;
  if (src.v || src.i || src.s) {
    const pb = (id, sfx) => `electrical.batteries.${id}.${sfx}`;
    const volts = src.v ? num(pb(src.v, 'voltage')) : null;
    const amps = src.i ? num(pb(src.i, 'current')) : null;
    const directW = src.v && src.v === src.i ? num(pb(src.i, 'power')) : null;
    const watts = directW ?? (volts !== null && amps !== null ? volts * amps : null);
    const soc = src.s ? socToPercent(rd(pb(src.s, 'capacity.stateOfCharge'))?.value) : null;
    const trRaw = src.s ? num(pb(src.s, 'capacity.timeRemaining')) : null;
    const tempK = src.v ? num(pb(src.v, 'temperature')) : null;
    const status =
      amps === null
        ? null
        : amps > 0.3
          ? 'Charging'
          : amps < -0.3
            ? 'Discharging'
            : 'Idle';
    battery = {
      id: src.v || src.s || src.i,
      name: hhValid ? 'House bank' : str(pb(src.s || src.v, 'name')),
      fromDashboard: Boolean(hhValid),
      volts,
      amps,
      watts,
      socPercent: soc,
      status,
      timeRemainingS:
        status === 'Discharging' && trRaw !== null && trRaw > 0 && trRaw < MAX_TIME_REMAINING_S ? trRaw : null,
      tempC: tempK !== null ? tempK - 273.15 : null,
    };
  }
  const dcSystemW = num('electrical.venus.dcPower');
  const invDcW =
    num('electrical.venus.vebusDcPower') ??
    (invGridW !== null && invLoadsW !== null ? invGridW - invLoadsW : null);

  // ---- Battery health (cell-level data from smart lithium modules) ----
  const moduleIds = instanceIds(paths, 'batteries', (leaf) => leaf.startsWith('cells.') || leaf.startsWith('balancer.'));
  const healthModules = moduleIds.map((id) => {
    const p = (sfx) => `electrical.batteries.${id}.${sfx}`;
    let minV = num(p('cells.voltage.minimum'));
    let maxV = num(p('cells.voltage.maximum'));
    if (minV === null || maxV === null) {
      const cells = paths
        .filter((x) => x.startsWith(`electrical.batteries.${id}.cells.`) && /\.cells\.\d+\.voltage$/.test(x))
        .map((x) => num(x))
        .filter((v) => v !== null);
      if (cells.length) {
        minV = Math.min(...cells);
        maxV = Math.max(...cells);
      }
    }
    const tRaw = num(p('temperature'));
    return {
      id,
      volts: num(p('voltage')),
      minV,
      maxV,
      spreadMv: minV !== null && maxV !== null ? Math.round((maxV - minV) * 1000) : null,
      tempC: tRaw === null ? null : tRaw > 150 ? tRaw - 273.15 : tRaw,
      balancer: str(p('balancer.status')),
    };
  });
  const allMin = healthModules.map((m) => m.minV).filter((v) => v !== null);
  const allMax = healthModules.map((m) => m.maxV).filter((v) => v !== null);
  const temps = healthModules.map((m) => m.tempC).filter((v) => v !== null);
  const balancers = healthModules.map((m) => m.balancer).filter(Boolean);
  const health = {
    modules: healthModules,
    minCellV: allMin.length ? Math.min(...allMin) : null,
    maxCellV: allMax.length ? Math.max(...allMax) : null,
    spreadMv: allMin.length && allMax.length ? Math.round((Math.max(...allMax) - Math.min(...allMin)) * 1000) : null,
    maxTempC: temps.length ? Math.max(...temps) : null,
    balanced: balancers.length ? balancers.every((b) => b.toLowerCase() === 'balanced') : null,
  };

  // ---- DC-DC chargers (Orion XS etc.) and other (non-inverter) chargers ----
  const dcdcByName = new Map();
  ['alternator', 'alternators', 'dcdc'].forEach((group) => {
    instanceIds(paths, group).forEach((id) => {
      const p = (sfx) => `electrical.${group}.${id}.${sfx}`;
      const nameR = rd(p('name'));
      const voltR = rd(p('voltage'));
      const name = nameR && isPresent(nameR.value) ? String(nameR.value) : `DC-DC ${id}`;
      const ts = Math.max(
        nameR?.timestamp ? new Date(nameR.timestamp).getTime() || 0 : 0,
        voltR?.timestamp ? new Date(voltR.timestamp).getTime() || 0 : 0
      );
      const device = {
        key: `${group}.${id}`,
        kind: 'dcdc',
        name,
        state: titleCase(str(p('chargingMode'))),
        inV: num(p('input.voltage')),
        inA: num(p('input.current')),
        inW: num(p('input.power')),
        outV: num(p('voltage')),
        outA: num(p('current')),
        outW: num(p('power')),
        ts,
      };
      // Same physical unit can appear under two groups (stale duplicate) — keep the freshest.
      const prevDev = dcdcByName.get(name);
      if (!prevDev || ts > prevDev.ts) dcdcByName.set(name, device);
    });
  });
  const inverterIdSet = new Set(inverters.map((i) => i.id));
  const otherChargers = instanceIds(paths, 'chargers', (leaf) => leaf === 'voltage' || leaf === 'current' || leaf === 'chargingMode')
    .filter((id) => !inverterIdSet.has(id))
    .map((id) => {
      const p = (sfx) => `electrical.chargers.${id}.${sfx}`;
      const outV = num(p('voltage'));
      const outA = num(p('current'));
      return {
        key: `chargers.${id}`,
        kind: 'charger',
        name: str(p('name')) || `Charger ${id}`,
        state: titleCase(str(p('chargingMode'))),
        inV: null,
        inA: null,
        inW: null,
        outV,
        outA,
        outW: num(p('power')) ?? (outV !== null && outA !== null ? outV * outA : null),
        ts: 0,
      };
    });
  const starterChargerId = config.hints?.starterChargerId || null;
  const starterCharger = starterChargerId ? otherChargers.find((c) => c.key === `chargers.${starterChargerId}`) || null : null;
  const starterVoltagePath = config.hints?.starterVoltagePath || null;
  const starterVoltage = starterVoltagePath ? num(starterVoltagePath) : null;
  const starter =
    starterVoltage !== null || starterCharger
      ? { volts: starterVoltage, charger: starterCharger }
      : null;
  const chargerDevices = [
    ...dcdcByName.values(),
    ...otherChargers.filter((c) => !starterCharger || c.key !== starterCharger.key),
  ];

  const eff = computeEfficiency({
    gridW,
    solarW,
    loadsW,
    batteryW: battery?.watts ?? null,
    dcLoadsW: dcSystemW,
  });

  const sourceRank = { live: 3, stale: 2, database: 1 };
  const bestSource = used.reduce(
    (acc, r) => (sourceRank[r.source] > sourceRank[acc] ? r.source : acc),
    'database'
  );
  const lastUpdate = used.reduce((acc, r) => {
    if (!r.timestamp) return acc;
    const t = new Date(r.timestamp).getTime();
    return !Number.isNaN(t) && t > acc ? t : acc;
  }, 0);

  return {
    grid,
    inverters,
    loads,
    solar,
    battery,
    batteryChoices,
    health,
    starter,
    chargerDevices,
    dcSystemW,
    inverterDcW: invDcW,
    efficiency: eff,
    flows: {
      gridToInverter: { dir: flowDirection(gridW), watts: gridW },
      inverterToLoads: { dir: flowDirection(loadsW), watts: loadsW },
      inverterToBattery: { dir: flowDirection(invDcW), watts: invDcW },
      solarToBattery: { dir: flowDirection(solarW), watts: solarW },
    },
    source: used.length ? bestSource : 'none',
    lastUpdate: lastUpdate ? new Date(lastUpdate).toISOString() : null,
    empty: used.length === 0,
  };
}

/** Rested LiFePO4 cells within ~30 mV are healthy; >80 mV deserves attention. */
export function cellHealthLevel(spreadMv, balanced) {
  if (spreadMv === null || spreadMv === undefined) return 'unknown';
  if (spreadMv > 80 || balanced === false && spreadMv > 50) return 'check';
  if (spreadMv > 30) return 'watch';
  return 'good';
}

// ---- Formatting ----
export function formatWatts(w) {
  if (w === null || w === undefined || Number.isNaN(w)) return { value: '--', unit: '' };
  const abs = Math.abs(w);
  if (abs >= 1000) return { value: (w / 1000).toFixed(abs >= 10000 ? 1 : 2), unit: 'kW' };
  return { value: String(Math.round(w)), unit: 'W' };
}

export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) return null;
  const mins = Math.round(seconds / 60);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ---- Demo sample (3-phase shore, two inverters) — only used when a vessel has no power data ----
function demoEntry(value, units = '') {
  return { value, units, timestamp: new Date().toISOString() };
}

export function buildDemoPowerFlat() {
  const f = {};
  const put = (p, v, u) => {
    f[p] = demoEntry(v, u);
  };
  put('electrical.venus.acSource', 'shore');
  put('electrical.ac.1.phase.A.lineNeutralVoltage', 231);
  put('electrical.ac.1.phase.A.current', 5.9);
  put('electrical.ac.1.phase.A.realPower', 1250);
  put('electrical.ac.1.phase.B.lineNeutralVoltage', 230);
  put('electrical.ac.1.phase.B.current', 5.1);
  put('electrical.ac.1.phase.B.realPower', 1070);
  put('electrical.ac.1.phase.C.lineNeutralVoltage', 232);
  put('electrical.ac.1.phase.C.current', 5.3);
  put('electrical.ac.1.phase.C.realPower', 1170);
  put('electrical.ac.1.total.realPower', 3490);
  put('electrical.ac.0.phase.A.lineNeutralVoltage', 230);
  put('electrical.ac.0.phase.A.current', 4.4);
  put('electrical.ac.0.phase.A.realPower', 980);
  put('electrical.ac.0.phase.B.lineNeutralVoltage', 230);
  put('electrical.ac.0.phase.B.current', 4.0);
  put('electrical.ac.0.phase.B.realPower', 900);
  put('electrical.ac.0.phase.C.lineNeutralVoltage', 230);
  put('electrical.ac.0.phase.C.current', 4.3);
  put('electrical.ac.0.phase.C.realPower', 1000);
  put('electrical.ac.0.total.realPower', 2880);
  ['1', '2'].forEach((n, i) => {
    const w = i === 0 ? 1790 : 1700;
    const wo = i === 0 ? 1490 : 1390;
    put(`electrical.inverters.${n}.name`, `Multi ${n}`);
    put(`electrical.inverters.${n}.inverterMode`, 'passthru');
    put(`electrical.inverters.${n}.acin.power`, w);
    put(`electrical.inverters.${n}.acin.voltage`, 231);
    put(`electrical.inverters.${n}.acin.current`, w / 231);
    put(`electrical.inverters.${n}.acout.power`, wo);
    put(`electrical.inverters.${n}.acout.voltage`, 230);
    put(`electrical.inverters.${n}.acout.current`, wo / 230);
    put(`electrical.chargers.${n}.current`, 24 + i * 2);
  });
  put('electrical.solar.0.name', 'MPPT 1');
  put('electrical.solar.0.panelPower', 640);
  put('electrical.solar.0.controllerMode', 'bulk');
  put('electrical.solar.0.yieldToday', 4.8 * 3.6e6, 'J');
  put('electrical.batteries.0.voltage', 52.4);
  put('electrical.batteries.0.current', 18.5);
  put('electrical.batteries.0.capacity.stateOfCharge', 0.78);
  put('electrical.venus.vebusDcPower', 1000);
  put('electrical.venus.dcPower', 120);
  return f;
}
